import React, { useEffect, useState } from "react";
import { ICommandBarItemProps, CommandBar } from "@fluentui/react";
import CustomButton from "../CustomButton";
import { GridCommandToggle } from "./utilities";

interface ISLEntriesGridCommandsProps {
  hide: boolean;
  post: () => void;
  addPosters: () => void;
  exportEntries: () => void;
  sendToPoster: () => void;
  approveEntries: () => void;
  needsClarification: () => void;
  recallEntries: () => void;
  deleteDrafts: () => void;
  deleteAttachments: () => void;
  addAttachmentActionIsValidFunction?: () => boolean;
  postDisabled: GridCommandToggle;
  addPostersDisabled: GridCommandToggle;
  exportEntriesDisabled: GridCommandToggle;
  sendToPosterDisabled: GridCommandToggle;
  approveEntriesDisabled: GridCommandToggle;
  needsClarificationDisabled: GridCommandToggle;
  recallEntriesDisabled: GridCommandToggle;
  deleteDraftsDisabled: GridCommandToggle;
  addAttachmentDisabled: GridCommandToggle;
  deleteAttachmentsDisabled: GridCommandToggle;
  onFilesSelected: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

interface ISLEntriesGridCommandsState {
  postDisabled: GridCommandToggle;
  addPostersDisabled: GridCommandToggle;
  exportEntriesDisabled: GridCommandToggle;
  sendToPosterDisabled: GridCommandToggle;
  approveEntriesDisabled: GridCommandToggle;
  needsClarificationDisabled: GridCommandToggle;
  recallEntriesDisabled: GridCommandToggle;
  deleteDraftsDisabled: GridCommandToggle;
  addAttachmentDisabled: GridCommandToggle;
  deleteAttachmentsDisabled: GridCommandToggle;
}

const initialState: ISLEntriesGridCommandsState = {
  postDisabled: GridCommandToggle.Disabled,
  addPostersDisabled: GridCommandToggle.Disabled,
  exportEntriesDisabled: GridCommandToggle.Disabled,
  sendToPosterDisabled: GridCommandToggle.Disabled,
  approveEntriesDisabled: GridCommandToggle.Disabled,
  needsClarificationDisabled: GridCommandToggle.Disabled,
  recallEntriesDisabled: GridCommandToggle.Disabled,
  deleteDraftsDisabled: GridCommandToggle.Disabled,
  addAttachmentDisabled: GridCommandToggle.Disabled,
  deleteAttachmentsDisabled: GridCommandToggle.Disabled
};

const SLEntriesGridCommands: React.FunctionComponent<ISLEntriesGridCommandsProps> = (props) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const triggerLoadAttachment = () => {
    const loadTheAttachments = async () => {
      if (inputRef && inputRef.current) {
        if (props.addAttachmentActionIsValidFunction) {
          const canOpenSelectionDialog = props.addAttachmentActionIsValidFunction();
          if (canOpenSelectionDialog) {
            inputRef.current.click();
          }
        } else {
          inputRef.current.click();
        }
      }
    };
    loadTheAttachments();
  };
  const [state, setState] = useState(initialState);

  useEffect(() => {
    setState((pState: ISLEntriesGridCommandsState) => {
      return {
        postDisabled: props.postDisabled ? props.postDisabled : pState.postDisabled,
        addPostersDisabled: props.addPostersDisabled ? props.addPostersDisabled : pState.addPostersDisabled,
        exportEntriesDisabled: props.exportEntriesDisabled ? props.exportEntriesDisabled : pState.exportEntriesDisabled,
        sendToPosterDisabled: props.sendToPosterDisabled ? props.sendToPosterDisabled : pState.sendToPosterDisabled,
        approveEntriesDisabled: props.approveEntriesDisabled
          ? props.approveEntriesDisabled
          : pState.approveEntriesDisabled,
        needsClarificationDisabled: props.needsClarificationDisabled
          ? props.needsClarificationDisabled
          : pState.needsClarificationDisabled,
        recallEntriesDisabled: props.recallEntriesDisabled ? props.recallEntriesDisabled : pState.recallEntriesDisabled,
        deleteDraftsDisabled: props.deleteDraftsDisabled ? props.deleteDraftsDisabled : pState.deleteDraftsDisabled,
        addAttachmentDisabled: props.addAttachmentDisabled ? props.addAttachmentDisabled : pState.addAttachmentDisabled,
        deleteAttachmentsDisabled: props.deleteAttachmentsDisabled
          ? props.deleteAttachmentsDisabled
          : pState.deleteAttachmentsDisabled
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const items: ICommandBarItemProps[] = [];
  if (state.postDisabled !== GridCommandToggle.Removed) {
    items.push({
      key: "post",
      name: "Post",
      ariaLabel: "Post selected entries",
      iconProps: { iconName: "Mail" },
      onClick: props.post,
      disabled: state.postDisabled === GridCommandToggle.Disabled
    });
  }
  if (state.addPostersDisabled !== GridCommandToggle.Removed) {
    items.push({
      key: "addPosters",
      name: "Add Posters",
      ariaLabel: "Add Poster or Posters to selected entries",
      iconProps: { iconName: "ConnectContacts" },
      onClick: props.addPosters,
      disabled: state.addPostersDisabled === GridCommandToggle.Disabled
    });
  }
  if (state.exportEntriesDisabled !== GridCommandToggle.Removed) {
    items.push({
      key: "exportToExcel",
      name: "Export to Excel",
      ariaLabel: "Export to Excel",
      iconProps: { iconName: "ExcelDocument" },
      onClick: props.exportEntries,
      disabled: state.exportEntriesDisabled === GridCommandToggle.Disabled
    });
  }
  if (state.sendToPosterDisabled !== GridCommandToggle.Removed) {
    items.push({
      key: "sendToPoster",
      name: "Send to Poster",
      ariaLabel: "Send Selected entries to Poster",
      iconProps: { iconName: "MailForward" },
      onClick: props.sendToPoster,
      disabled: state.sendToPosterDisabled === GridCommandToggle.Disabled
    });
  }
  if (state.approveEntriesDisabled !== GridCommandToggle.Removed) {
    items.push({
      key: "approve",
      name: "Approve",
      ariaLabel: "Approve Selected entries",
      iconProps: { iconName: "DocumentApproval" },
      onClick: props.approveEntries,
      disabled: state.approveEntriesDisabled === GridCommandToggle.Disabled
    });
  }
  if (state.needsClarificationDisabled !== GridCommandToggle.Removed) {
    items.push({
      key: "needsClarification",
      name: "Needs Clarification",
      ariaLabel: "Request clarification on selected entries",
      iconProps: { iconName: "SurveyQuestions" },
      onClick: props.needsClarification,
      disabled: state.needsClarificationDisabled === GridCommandToggle.Disabled
    });
  }
  if (state.recallEntriesDisabled !== GridCommandToggle.Removed) {
    items.push({
      key: "recall",
      name: "Recall",
      iconProps: { iconName: "Undo" },
      ariaLabel: "Recall selected entries from Reviewer",
      onClick: props.recallEntries,
      disabled: state.recallEntriesDisabled === GridCommandToggle.Disabled
    });
  }
  if (state.addAttachmentDisabled !== GridCommandToggle.Removed) {
    items.push({
      key: "addAttachments",
      name: "Add Attachments",
      ariaLabel: "Add Attachment to selected entries",
      iconProps: { iconName: "Attach" },
      onClick: triggerLoadAttachment,
      disabled: state.addAttachmentDisabled === GridCommandToggle.Disabled
    });
  }
  if (state.addAttachmentDisabled !== GridCommandToggle.Removed) {
    items.push({
      key: "deleteAttachments",
      name: "Delete Attachments",
      ariaLabel: "Delete all Attachments to selected entries",
      iconProps: { iconName: "RemoveLink" },
      onClick: props.deleteAttachments,
      disabled: state.addAttachmentDisabled === GridCommandToggle.Disabled
    });
  }
  if (state.deleteDraftsDisabled !== GridCommandToggle.Removed) {
    items.push({
      key: "delete",
      name: "Delete",
      ariaLabel: "Delete selected entries",
      iconProps: { iconName: "Delete" },
      onClick: props.deleteDrafts,
      disabled: state.deleteDraftsDisabled === GridCommandToggle.Disabled
    });
  }
  return (
    <React.Fragment>
      <input
        style={{ display: "none" }}
        type="file"
        onChange={props.onFilesSelected}
        name="InputFile"
        ref={inputRef}
        accept=".doc, .xls, .ppt, .docx, .xlsx, .pptx, .msg, .png, .txt, .csv"
        multiple={true}
        aria-label="Select files to attach"
      />
      {props.hide ? null : (
        <CommandBar
          buttonAs={CustomButton}
          items={items}
          ariaLabel={"Actions to perform on selected SL Entries"}
          overflowButtonProps={{ ariaLabel: "More Actions" }}
        />
      )}
    </React.Fragment>
  );
};

export default SLEntriesGridCommands;
